import React, { useState, useRef, useEffect } from 'react';
import icon from '@ingka/ssr-icon/paths/magnifying-glass';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import styled from 'styled-components';
import useTranslation from '../../hooks/useTranslation';
import { addressAutocompleteService } from '../../services';

const AddressContainer = styled.div`
  position: relative;
`;

const AddressCard = styled.div`
  background-color: #f7f7f7;
  border-radius: 8px;
  padding: 16px;
  font-family: 'Noto IKEA', 'Noto Sans', 'Roboto', 'Open Sans', sans-serif;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const AddressText = styled.p`
  margin: 0 0 8px;
  font-size: 1rem;
  color: #333;
`;

const SearchContainer = styled.div`
  font-family: 'Noto IKEA', 'Noto Sans', 'Roboto', 'Open Sans', sans-serif;
`;

const Dropdown = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: -20px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: #fff;

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #aaa;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #888;
  }
`;

const DropdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #eaeaea;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f1f1f1;
  }
`;

const ItemInfo = styled.div`
  max-width: calc(100% - 30px);
`;

const ItemText = styled.p`
  display: block;
  color: #333;
  font-size: 1rem;
`;

const SubText = styled.p`
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #666;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  color: #888;
  font-size: 1.125rem;
`;

const Suggestion = styled.div`
  margin-top: -20px;
  padding: 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #666;
`;

const ValidationMessage = styled.div`
  display: flex;
  align-items: center;
  color: rgb(224, 7, 81); /* Matching the validation error color */
  font-size: 0.75rem;
  margin-top: -17px;

  span {
    display: inline-block;
    width: 16px;
    height: 16px;
    font-weight: bold;
    margin-right: 6px;
    background-color: rgb(224, 7, 81); /* Red circle */
    border-radius: 50%; /* Circle shape */
    position: relative;
    text-align: center;
    line-height: 16px; /* Vertically center the content */
    font-size: 0.75rem; /* Adjust size of the exclamation mark */
    color: white; /* White exclamation mark */
  }
`;

export type AddressDetails = {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  postalCode: string;
  state: string;
};

export const AddressAutoCompleteComponent = ({
  onAddressSelect,
  submitAddressFlag,
}: {
  onAddressSelect: (details: AddressDetails) => void;
  submitAddressFlag: boolean;
}) => {
  const [value, setValue] = useState<string>('');
  const [loadingFlag, setLoadingFlag] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [suggestedData, setSuggestedData] = useState<
    { name?: string; value?: string }[]
  >([]);

  const [isAddressSelected, setIsAddressSelected] = useState<boolean>(false);
  const [moreInputRequired, setMoreInputRequired] = useState<boolean>(false);

  const [selectedAddress, setSelectedAddress] = useState<AddressDetails>({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    postalCode: '',
    state: '',
  });
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAddressSelected && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      input.selectionStart = input.selectionEnd = input.value.length;
    }
  }, [isAddressSelected]);

  const mapSelectedAddress = async (item: any) => {
    if (item.isCapturable === true) {
      const res = await addressAutocompleteService.calladdressautocomplete({
        captureId: item.captureId,
        freeText: item.text,
      });
      setIsAddressSelected(true);
      setSuggestedData([]);
      const selectedAddress = {
        addressLine1: res.addressLine1,
        addressLine2: res.addressLine2,
        addressLine3: res.addressLine3,
        city: res.city,
        postalCode: res.postalCode,
        state: res.state,
      };
      setSelectedAddress(selectedAddress);
      setValue(
        `${res.addressLine1} ${res.addressLine2} ${res.addressLine3} ${res.city} ${res.postalCode} ${res.state}`
      );
      onAddressSelect(selectedAddress);
    }
    if (item.isCapturable === false) {
      const res = await addressAutocompleteService.calladdressautocomplete({
        captureGroupId: item.captureGroupId,
        freeText: item.text,
      });
      if (res && res.suggestions) {
        setSuggestedData(res.suggestions);
      } else {
        setSuggestedData([]);
      }
      setValue(`${item.text}`);
    }
  };

  const onFieldChange = (event: Event) => {
    setSuggestedData([]);
    setLoadingFlag(false);
    setMoreInputRequired(false);
    setIsAddressSelected(false);

    const target = event.target as HTMLInputElement;
    const freeText = target.value;

    setValue(freeText);

    if (!freeText) {
      setSuggestedData([]);
      return;
    } else if (freeText.length < 3) {
      setMoreInputRequired(true);
      setSuggestedData([]);
      return;
    } else if (freeText.length >= 3) {
      setLoadingFlag(true);
      addressAutocompleteService
        .calladdressautocomplete({
          freeText,
        })
        .then((res) => {
          console.log(res);
          setSuggestedData(res.suggestions);
          setLoadingFlag(false);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex < suggestedData.length - 1 ? prevIndex + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestedData.length - 1
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestedData.length) {
        mapSelectedAddress(suggestedData[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      if (suggestedData.length > 0) {
        event.preventDefault();
        setSuggestedData([]);
        setActiveIndex(-1);
      }
    }
  };

  return (
    <AddressContainer>
      <SearchContainer>
        <FormField>
          <InputField
            id="address-autocomplete"
            type="text"
            label={t('spareParts.AddressLabel')}
            ssrIcon={icon}
            iconPosition="leading"
            value={value}
            ref={inputRef}
            onChange={onFieldChange}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-label={t('spareParts.AddressLabel')}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-controls={
              suggestedData.length ? 'suggestions-list' : undefined
            }
            aria-expanded={!!suggestedData.length}
            aria-activedescendant={
              activeIndex >= 0 ? `suggestion-item-${activeIndex}` : undefined
            }
            className={`${submitAddressFlag ? 'input-invalid' : 'input-valid'}`}
            autoComplete="off"
            name="no-autofill"
            required
          />
        </FormField>

        {submitAddressFlag &&
          !moreInputRequired &&
          !loadingFlag &&
          !isAddressSelected &&
          suggestedData.length === 0 && (
            <ValidationMessage role="alert">
              <span aria-hidden="true">!</span>{' '}
              {t('spareParts.AddressfieldValidation')}
            </ValidationMessage>
          )}

        {moreInputRequired ? (
          <Suggestion aria-live="polite">{t('spareParts.typeMore')}</Suggestion>
        ) : (
          ''
        )}
        {loadingFlag ? (
          <Suggestion>{t('spareParts.loadingData')}</Suggestion>
        ) : (
          ''
        )}
        {suggestedData.length ? (
          <Dropdown id="suggestions-list" role="listbox">
            {suggestedData.map((item: any, index: number) => (
              <DropdownItem
                key={item.value ? item.value : `suggestion-${index}`}
                id={`suggestion-item-${index}`}
                role="option"
                tabIndex={-1}
                aria-selected={activeIndex === index}
                onClick={() => mapSelectedAddress(item)}
                style={{
                  backgroundColor:
                    activeIndex === index ? '#f1f1f1' : 'transparent',
                }}
              >
                <ItemInfo>
                  <ItemText>{item.text}</ItemText>
                  <SubText>{item.subText}</SubText>
                </ItemInfo>
                <ArrowIcon aria-hidden="true">&#10132;</ArrowIcon>
              </DropdownItem>
            ))}
          </Dropdown>
        ) : (
          ''
        )}
      </SearchContainer>
      {isAddressSelected ? (
        <AddressCard>
          <AddressText>{`${selectedAddress.addressLine1} ${selectedAddress.addressLine2} ${selectedAddress.addressLine3}`}</AddressText>
          <AddressText>{`${selectedAddress.city} ${selectedAddress.postalCode} ${selectedAddress.state}`}</AddressText>
        </AddressCard>
      ) : (
        ''
      )}
    </AddressContainer>
  );
};
